import HeaderSection from '@/components/Header/HeaderSection.vue';
import PortfolioSuperSetChart from '@/components/PortfolioSuperSetChart/PortfolioSuperSetChart.vue';
import PortfolioSidebar from '@/components/PortfolioSidebar/PortfolioSidebar.vue';
import BolideAssistant from '@/components/BolideAssistant/BolideAssistant.vue';
import { get_token } from '@/router';
import {
  main_dashboard_id,
  social_impact_intelligence_dashboard_id,
  role_dashboards
} from '@/config/dashboardIds';
import BolideLoader from '@/components/BolideLoader/BolideLoader.vue';

// Environment variables
const superset_base_domain = process.env.VUE_APP_SUPERSET_BASE_URL;
const guest_token_url = process.env.VUE_APP_GUEST_TOKEN_URL;
const superset_sdk_url = process.env.VUE_APP_SUPERSET_SDK_URL;

// Superset Config
const SUPERSET_CONFIG = {
  baseUrl: superset_base_domain,
  defaultDashboardIds: {
    main: main_dashboard_id
  }
};

const AUTH_CONFIG = {
  guestTokenUrl: guest_token_url
};

// Token Cache
const TOKEN_CACHE = {
  tokens: new Map(),

  get(dashboardId) {
    const cached = this.tokens.get(dashboardId);
    if (cached && Date.now() < cached.expiresAt) return cached.token;
    if (cached) this.tokens.delete(dashboardId);
    return null;
  },

  set(dashboardId, token, expiresInMs = 55 * 60 * 1000) {
    this.tokens.set(dashboardId, {
      token,
      expiresAt: Date.now() + expiresInMs
    });
  },

  clear() { this.tokens.clear(); },

  cleanup() {
    const now = Date.now();
    for (const [dashboardId, data] of this.tokens.entries()) {
      if (now >= data.expiresAt) this.tokens.delete(dashboardId);
    }
  }
};

function safeParseMaybeDoubleEncodedJSON(text) {
  let parsed = JSON.parse(text);
  if (typeof parsed === 'string') parsed = JSON.parse(parsed);
  return parsed;
}

export default {
  name: 'DashboardView',

  // Sidebars read the collapsed state from here (they used to look for it on $root, where it never existed)
  provide() {
    return ({
      is_sidebar_collapsed: () => this.sidebar_collapsed
    });
  },

  components: {
    HeaderSection,
    PortfolioSuperSetChart,
    PortfolioSidebar,
    BolideAssistant,
    BolideLoader
  },

  data() {
    return ({
      active_menu_item: 'social_impact_intelligence',
      role_key: 'fund_manager',

      // Data
      breadcrumb: [],

      // UI State
      iframeLoading: false,
      iframeError: false,
      rightPanelCollapsed: false,
      isMobile: false,

      // Superset State
      sdkLoaded: false,
      sdkConfigured: false,
      initializationComplete: false,
      authToken: get_token(),
      tokenRefreshInProgress: false,
      showAssistant: false,
      sidebar_collapsed: false,
      min_loader_time: 3500,
      loader_start_time: null,
      is_loading: true,
      is_mobile: window.innerWidth < 768,
      mobile_sidebar_open: false,

      role_dashboards: role_dashboards
    });
  },

  computed: {
    current_dashboard_id() {
      const role_dashboards_map = this.role_dashboards[this.role_key] || this.role_dashboards.fund_manager;
      return role_dashboards_map[this.active_menu_item] || role_dashboards_map.default || social_impact_intelligence_dashboard_id;
    }
  },


  mounted() {
    this.set_role_dashboards();

    this.is_loading = true;
    this.loader_start_time = Date.now();  // Record loader start moment

    this.$nextTick(() => {
      this.initializeDashboard();
    });

    // Fallback if superset never loads
    setTimeout(() => {
      if (this.is_loading) {
        this.is_loading = false;
      }
    }, 10000); // 10s fallback

    // 5️⃣ Setup responsiveness
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    // 6️⃣ Set initial breadcrumb
    this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];

    // 7️⃣ Token cleanup
    this.tokenCleanupInterval = setInterval(() => {
      TOKEN_CACHE.cleanup();
    }, 60000);
  },

  beforeUnmount() {
    this.cleanupDashboards();
    if (this.tokenCleanupInterval) clearInterval(this.tokenCleanupInterval);
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    set_role_dashboards() {
      const stored_role = sessionStorage.getItem('role') || '';
      const normalized_role = stored_role.toString().trim().toLowerCase().replace(/\s+/g, '_');
      this.role_key = this.role_dashboards[String(normalized_role)] ? normalized_role : 'fund_manager';

      const role_dashboards_map = this.role_dashboards[this.role_key] || this.role_dashboards.fund_manager;
      if (!role_dashboards_map[this.active_menu_item]) {
        const first_key = Object.keys(role_dashboards_map).find(key => key !== 'default');
        this.active_menu_item = first_key || this.active_menu_item;
      }
    },
    toggleSidebar() {
      this.sidebar_collapsed = !this.sidebar_collapsed;
    },

    handleResize() {
      this.isMobile = window.innerWidth < 992;
      this.is_mobile = window.innerWidth < 768;
      if (this.isMobile) this.rightPanelCollapsed = false;
    },

    toggle_mobile_sidebar() {
      this.mobile_sidebar_open = !this.mobile_sidebar_open;
    },
    close_sidebar() {
      this.mobile_sidebar_open = false;
    },
    // ========== DASHBOARD INITIALIZATION ==========
    async initializeDashboard() {
      try {
        this.iframeLoading = true;
        this.iframeError = false;
        this.authToken = get_token();

        if (!this.authToken) throw new Error('No authentication token found.');

        await this.initializeSupersetSDK();
        await this.configureSupersetSDK();
        await this.embedDashboard('dashboard-main', this.current_dashboard_id);

        // Superset is ready → hide loader
        const elapsed = Date.now() - this.loader_start_time;

        if (elapsed >= this.min_loader_time) {
          // Show loader for at least min time
          this.is_loading = false;
        } else {
          // Add delay to complete minimum duration
          const remaining = this.min_loader_time - elapsed;
          setTimeout(() => {
            this.is_loading = false;
          }, remaining);
        }

        this.initializationComplete = true;

      } catch (error) {
        console.error('Dashboard initialization failed:', error);
        this.iframeError = true;
        this.iframeLoading = false;
      }
    },

    async fetchGuestToken(dashboardId = null, { allowMainDashboardFallback = false } = {}) {
      if (this.tokenRefreshInProgress) {
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            if (!this.tokenRefreshInProgress) {
              clearInterval(checkInterval);
              resolve();
            }
          }, 100);
        });

        const cachedToken = TOKEN_CACHE.get(dashboardId);
        if (cachedToken) return { token: cachedToken };
      }

      this.tokenRefreshInProgress = true;

      try {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;
        if (!this.authToken) {
          this.authToken = get_token();
        }

        const cachedToken = TOKEN_CACHE.get(targetDashboardId);
        if (cachedToken) return { token: cachedToken };

        const response = await fetch(AUTH_CONFIG.guestTokenUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${this.authToken}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify({ dashboard_id: targetDashboardId, client_app: 'vsi_perpetual' })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Guest token request failed: ${response.status}, ${errorText}`);
        }

        const rawResponse = await response.text();
        const data = safeParseMaybeDoubleEncodedJSON(rawResponse);

        if (data.status === 'success' && data.data && data.data.token) {
          TOKEN_CACHE.set(targetDashboardId, data.data.token);
          return data.data;
        } else {
          throw new Error(data.message || 'Failed to get guest token');
        }
      } catch (error) {
        const targetDashboardId = dashboardId || SUPERSET_CONFIG.defaultDashboardIds.main;

        if (
          allowMainDashboardFallback &&
          targetDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main
        ) {
          return this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main);
        }

        throw new Error(`Failed to fetch guest token: ${error.message}`);
      } finally {
        this.tokenRefreshInProgress = false;
      }
    },

    async fetch_guest_token() {
      const currentDashboardId = this.current_dashboard_id;
      try {
        const tokenData = await this.fetchGuestToken(currentDashboardId, {
          allowMainDashboardFallback: true
        });
        if (!tokenData || !tokenData.token) throw new Error('No token received');
        return tokenData.token;
      } catch (error) {
        if (currentDashboardId !== SUPERSET_CONFIG.defaultDashboardIds.main) {
          const fallbackTokenData = await this.fetchGuestToken(SUPERSET_CONFIG.defaultDashboardIds.main);
          return fallbackTokenData.token;
        }
        throw error;
      }
    },

    async initializeSupersetSDK() {
      if (typeof window.supersetEmbeddedSdk === 'undefined') await this.loadSupersetSDK();
      this.sdkLoaded = true;
      return true;
    },

    loadSupersetSDK() {
      return new Promise((resolve, reject) => {
        if (typeof window.supersetEmbeddedSdk !== 'undefined') {
          resolve();
          return;
        }
        const script = document.createElement('script');
        script.src = superset_sdk_url;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Superset SDK'));
        document.head.appendChild(script);
      });
    },

    async configureSupersetSDK() {
      if (!window.supersetEmbeddedSdk) throw new Error('Superset SDK not loaded');
      this.sdkConfigured = true;
    },

    // ========== DASHBOARD EMBEDDING ==========
    async embedDashboard(containerId, dashboardId, forceRefreshToken = true) {
      try {
        this.iframeLoading = true;
        this.iframeError = false;

        const mountPoint = document.getElementById(containerId);
        if (!mountPoint) throw new Error(`Mount point not found: ${containerId}`);

        mountPoint.innerHTML = '';

        // Clear cached token if forcing refresh
        if (forceRefreshToken) {
          TOKEN_CACHE.tokens.delete(dashboardId);
        }

        const guestTokenData = await this.fetchGuestToken(dashboardId);

        if (!window.supersetEmbeddedSdk) {
          await this.initializeSupersetSDK();
          await this.configureSupersetSDK();
        }

        await window.supersetEmbeddedSdk.embedDashboard({
          id: dashboardId,
          supersetDomain: SUPERSET_CONFIG.baseUrl,
          mountPoint: mountPoint,
          fetchGuestToken: async () => {
            const tokenData = await this.fetchGuestToken(dashboardId, {
              allowMainDashboardFallback: true
            });
            return tokenData.token || guestTokenData.token;
          },
          dashboardUiConfig: {
            hideTitle: true,
            hideTab: true,
            filters: { visible: false, expanded: false }
          }
        });

        // Wait until Superset iframe signals it's ready
        window.addEventListener("message", event => {
          if (event.data?.type === "dashboardLoadComplete" ||
            event.data?.event === "dashboard_loaded") {

            // Hide loader if still visible
            if (this.is_loading) {
              this.is_loading = false;
            }
          }
        });
        this.iframeLoading = false;
      } catch (error) {
        console.error('Failed to embed dashboard:', error);
        this.iframeLoading = false;
        this.iframeError = true;
      }
    },

    cleanupDashboards() {
      const container = document.getElementById('dashboard-main');
      if (container) container.innerHTML = '';
    },

    // ========== NAVIGATION METHODS ==========
    handleBreadcrumbClick(breadcrumbId) {
      if (breadcrumbId === 'main') {
        this.active_menu_item = 'social_impact_intelligence';
        this.handleGlobalMenuSelected('social_impact_intelligence');
      }
    },

    async goBack() {
      this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];
      this.cleanupDashboards();
      this.active_menu_item = 'social_impact_intelligence';

      TOKEN_CACHE.clear();

      this.$nextTick(async () => {
        try {
          await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load main dashboard:', error);
        }
      });
    },

    async handleGlobalMenuSelected(data) {
      const menuItem = typeof data === 'string' ? data : data.key;
      const label = typeof data === 'object' ? data.label : null;

      this.active_menu_item = menuItem;

      TOKEN_CACHE.cleanup();

      if (menuItem === 'social_impact_intelligence') {
        this.breadcrumb = [{ id: 'main', title: 'Dashboard' }];
      } else if (label) {
        this.breadcrumb = [
          { id: 'main', title: 'Dashboard' },
          { id: menuItem, title: label }
        ];
      }

      this.$nextTick(async () => {
        try {
          await this.embedDashboard('dashboard-main', this.current_dashboard_id, true);
        } catch (error) {
          console.error('Failed to load global dashboard:', error);
        }
      });
    },

    getMainContentClass() {
      if (this.isMobile) return 'col-12 h-100';
      return this.rightPanelCollapsed ? 'col-12 h-100' : 'col-lg-12 col-md-12 m-0 p-0 h-100';
    },

    toggleRightPanel() {
      if (this.isMobile) return;
      this.rightPanelCollapsed = !this.rightPanelCollapsed;
    }
  }
};
